import { useState, useMemo, useCallback } from 'react'
import { Table, Input, Row, Col, Modal, Button, Popconfirm } from 'antd'
import { SearchOutlined, CloseOutlined } from '@ant-design/icons'
import ChannelInput from '../../../Components/Omnichannel/ChannelInput'
import { getHtmlParentElement, renderTranslation, stopPropagation } from '../../../Helpers/GenericHelpers'
import {
  ShippingTableCols,
  shippingTableISO,
  shippingTableCountry,
  shippingTableAmount,
} from '../../../Constants/TableConfiguration'
import { eraseChannelMultishipping } from '../../../Slices/Settings/ChannelSlice'
import { useAppDispatch, useAppSelector } from '../../../hooks'
import { TranslationKeys } from 'Constants/TranslationKeys'
import { ChannelSettingsReset } from 'Constants/Settings'

interface ShippingModalProps {
  bodyref: React.MutableRefObject<undefined>
  setShippingModal: (arg: boolean) => unknown
  selectedChannel: string
  shippingModal: boolean
}

function ShippingModal(props: ShippingModalProps) {
  const dispatch = useAppDispatch()
  const language = useAppSelector(state => state.present.language.value)
  const channelList = useAppSelector(state => state.present.channel.channelList)
  const countries = useAppSelector(state => state.present.channel.countriesList)
  const [countrySearch, setCountrySearch] = useState('')

  const filterCountryBySearchValue = useCallback(
    countriesData => {
      if (!countrySearch || countrySearch.length < 2) return countriesData

      const lowerSearchValue = countrySearch.toLowerCase()
      return countriesData.filter(x =>
        x[shippingTableCountry] ? x[shippingTableCountry].toLowerCase().includes(lowerSearchValue) : false,
      )
    },
    [countrySearch],
  )

  const shippingTableColumns = useMemo(() => {
    const shippingCol = []
    ShippingTableCols.forEach(x => {
      if (x === shippingTableISO) {
        shippingCol.push({
          title: (
            <div className='d-flex justify-center'>
              {renderTranslation(language, TranslationKeys.multishippingModalCode)}
            </div>
          ),
          dataIndex: x,
          key: x,
          width: 130,
          render: text => {
            return <div className='d-flex justify-center channel-text'>{text}</div>
          },
        })
      } else if (x === shippingTableCountry) {
        shippingCol.push({
          title: (
            <div className='channel-table-search'>
              <Input
                variant={'borderless'}
                prefix={<SearchOutlined className='general-icon input-search-icon' />}
                placeholder={renderTranslation(language, TranslationKeys.multishippingModalNation)}
                className='input-search channel-search'
                value={countrySearch}
                onChange={e => {
                  setCountrySearch(e.target.value)
                }}
                onClick={stopPropagation}
                allowClear={{
                  clearIcon: <CloseOutlined className='general-icon input-clear-icon' />,
                }}
              />
            </div>
          ),
          dataIndex: x,
          key: x,
          render: text => {
            return <div className='channel-text pl-3'>{text}</div>
          },
        })
      } else if (x === shippingTableAmount) {
        shippingCol.push({
          title: (
            <div className='d-flex justify-center'>
              {renderTranslation(language, TranslationKeys.multishippingModalAmount)}
            </div>
          ),
          dataIndex: x,
          key: x,
          width: 130,
          render: (text, record) => {
            const { iso, name, amount } = record
            return (
              <Row align={'middle'} justify={'center'}>
                <ChannelInput channelName={props.selectedChannel} value={amount} iso={iso} country={name} />
              </Row>
            )
          },
        })
      }
    })

    return shippingCol
  }, [props.selectedChannel, countrySearch])

  const mappedCountries = useMemo(() => {
    const filteredChannel = channelList.filter(x => x.name === props.selectedChannel)
    if (!filteredChannel[0] || !filteredChannel[0].multiShipping) {
      return countries
    }

    return countries.map(country => {
      const shippingAmount = filteredChannel[0].multiShipping.find(shipping => shipping.iso === country.iso)?.amount
      return {
        ...country,
        amount: shippingAmount || null,
      }
    })
  }, [channelList, props.selectedChannel, countries])

  return (
    <Modal
      className='multi-shipping-modal'
      open={props.shippingModal}
      onCancel={() => props.setShippingModal(false)}
      width={800}
      closeIcon={<CloseOutlined className='multi-shipping-close-icon' />}
      footer={[
        <Row justify={'space-between'} align={'middle'}>
          <Col span={12} key='done-button'>
            <Button
              className={'exclude-modal-trigger multi-shipping-confirm-button'}
              onClick={() => props.setShippingModal(false)}
            >
              {renderTranslation(language, TranslationKeys.backTextMultishippingModal)}
            </Button>
          </Col>
          <Col span={12} key='reset-button'>
            <Popconfirm
              title={renderTranslation(language, TranslationKeys.resetMultishippingExceptionsPopconfirmTitle)}
              description={renderTranslation(
                language,
                TranslationKeys.resetMultishippingExceptionsPopconfirmDescription,
              )}
              onConfirm={() => dispatch(eraseChannelMultishipping(props.selectedChannel))}
              onCancel={stopPropagation}
              okText={renderTranslation(language, TranslationKeys.configurationDropdownPopYes)}
              cancelText={renderTranslation(language, TranslationKeys.configurationDropdownPopNo)}
              getPopupContainer={getHtmlParentElement}
            >
              <Button className={'exclude-modal-trigger multi-shipping-reset-button'} onClick={stopPropagation}>
                {ChannelSettingsReset}
              </Button>
            </Popconfirm>
          </Col>
        </Row>,
      ]}
      getContainer={props.bodyref.current}
    >
      <Table
        className='multi-shipping-table'
        rowKey={record => {
          return 'key-' + record.name
        }}
        columns={shippingTableColumns}
        dataSource={filterCountryBySearchValue(mappedCountries)}
        pagination={false}
        scroll={{ y: '60vh' }}
      />
    </Modal>
  )
}

export default ShippingModal
